import type { OrderFormItem } from "@decocms/apps-vtex/utils/types"
import type { PropertyValue } from "@decocms/apps-commerce/types"
import type { ExtendedProductItem, ProductItem } from "~/sdk/ga4/types/index"

/**
 * Regra única de `item_id` / `item_variant` do funil (#284).
 *
 * - `item_id` = `${productId}_${skuId}` (ex. `15787_60650`). É o formato que o
 *   container GTM-5PCJKR3 consome: o BTG360 faz `item_id.split("_")[0]`, a
 *   variável GA4 de carrinho/checkout testa `/^\d+_\d+$/` e a tag de view_item
 *   de variação compara `item_id === group + "_" + sku`.
 * - `item_variant` = nome da variação do SKU (ex. `Camomila`), os valores das
 *   especificações de SKU na ordem do catálogo, unidos por ` / ` quando há mais
 *   de uma dimensão (ex. `Preto / M`). Produto sem variação = `""`.
 *
 * Antes, a vitrine mandava o NOME do SKU como variante (`variant: name`, que na
 * Miess é o nome do produto) e o minicart não mandava nada (`""`).
 */
export const VARIANT_SEPARATOR = " / "

export const toItemId = (productGroupId: string | undefined, sku: string | undefined) =>
    `${productGroupId ?? ""}_${sku ?? ""}`

// Mesmo descarte do `useVariantPossibilities` (apps-commerce): o que sobra no
// `additionalProperty` de um SKU são as dimensões de variação.
const NOT_A_DIMENSION = new Set(["category", "cluster", "RefId", "descriptionHtml"])

/**
 * Nome da variação a partir do `additionalProperty` do SKU (schema.org).
 *
 * Aceita `valueReference` ausente porque o `trimProductForShelf` da vitrine
 * remove esse campo (fica só name/value); com ele presente, só SPECIFICATION
 * conta (anexos, ReferenceID etc. ficam de fora).
 */
export function variantFromAdditionalProperty(props: PropertyValue[] | undefined): string {
    const values: string[] = []
    for (const { name, value, valueReference } of props ?? []) {
        if (!name || NOT_A_DIMENSION.has(name)) continue
        if (valueReference && valueReference !== "SPECIFICATION") continue
        if (typeof value !== "string" || !value.trim()) continue
        values.push(value.trim())
    }
    return values.join(VARIANT_SEPARATOR)
}

/**
 * Nome da variação a partir do endpoint de variações do catálogo
 * (`skus[].dimensions` + a ordem de `dimensions`).
 */
export function variantFromDimensions(
    dimensions: Record<string, string> | undefined,
    order: string[] = Object.keys(dimensions ?? {}),
): string {
    return order
        .map((name) => dimensions?.[name]?.trim())
        .filter((value): value is string => !!value)
        .join(VARIANT_SEPARATOR)
}

type CartItem = Pick<
    OrderFormItem,
    "id" | "productId" | "name" | "seller" | "listPrice" | "price" | "detailUrl"
> & {
    additionalInfo?: { brandName?: string | null } | null
    productCategories?: Record<string, string> | null
}

/** Item do orderForm → `ProductItem` do transform GA4 (minicart). */
export function orderFormItemToProductItem(
    item: CartItem,
    extended: ExtendedProductItem,
    variant = "",
): ProductItem {
    return {
        sku: item.id,
        inProductGroupWithID: item.productId,
        name: item.name,
        seller: item.seller,
        listPrice: parseFloat((item.listPrice / 100).toFixed(2)),
        price: parseFloat((item.price / 100).toFixed(2)),
        url: `${globalThis.location?.host ?? ""}${item.detailUrl}`,
        brand: item.additionalInfo?.brandName ?? "",
        category: Object.values(item.productCategories ?? {}).join(">"),
        variant,
        extended,
    }
}

interface CatalogVariations {
    dimensions?: string[]
    skus?: Array<{ sku: number | string; dimensions?: Record<string, string> }>
}

// O orderForm não traz as especificações do SKU (`skuSpecifications` vem
// null na Miess), então o minicart busca o rótulo no catálogo, uma vez por
// produto e por carregamento de página.
const variationsByProduct = new Map<string, Promise<Record<string, string>>>()

function fetchProductVariants(productId: string): Promise<Record<string, string>> {
    let pending = variationsByProduct.get(productId)
    if (!pending) {
        pending = fetch(`/api/catalog_system/pub/products/variations/${encodeURIComponent(productId)}`)
            .then((res) => {
                if (!res.ok) throw new Error(`variations ${res.status}`)
                return res.json() as Promise<CatalogVariations>
            })
            .then((data) => {
                const bySku: Record<string, string> = {}
                for (const sku of data?.skus ?? []) {
                    bySku[String(sku.sku)] = variantFromDimensions(sku.dimensions, data?.dimensions)
                }
                return bySku
            })
            .catch(() => {
                variationsByProduct.delete(productId)
                return {}
            })
        variationsByProduct.set(productId, pending)
    }
    return pending
}

/** `skuId → item_variant` para os itens do carrinho. Falha vira `""`. */
export async function getCartItemVariants(
    items: Array<Pick<OrderFormItem, "id" | "productId">>,
): Promise<Record<string, string>> {
    const productIds = [...new Set(items.map((item) => item.productId).filter(Boolean))]
    const maps = await Promise.all(productIds.map(fetchProductVariants))
    return Object.assign({}, ...maps)
}

/** Só para testes. */
export const __resetCartVariantsCache = () => variationsByProduct.clear()
